/**
 * **가게 신청 서류 — 어휘·주소·말·판정 한 곳**(계획 `docs/planning/callpop-web-signup-20261029.md` §11 · W10~W17). 의존성 없는 순수
 * 모듈이다(Vue·Element·`@/` 별칭 금지 — 단위 시험이 node 에서 그대로 읽는다).
 *
 * 서류 층은 업무 이름이 없는 일반 이름이다(W17 — 서버 표 `tbl_store_application*`, 첫 종류 `CALLPOP_JOIN`). 종류의 화면 이름(「콜팝 가입」)은
 * 서버가 `kindName` 으로 준다 — 화면은 종류마다 이름을 다시 적지 않는다. 이 파일이 갖는 것:
 *
 * - 경로·API·액션 이름(§11.3·§11.4·§11.5·§11.9 — 라우트의 `requiredAction` 과 서버 권한 SQL 이 같은 글자를 본다)
 * - 서류 종류·상태 어휘와 그 말(§11.1). 상태·n/3 은 서버가 서류 행에서 계산해 준다 — 화면은 다시 판정하지 않는다
 * - 사장님 화면(`/v/store/applications`)과 휴대폰 화면(`/docs/upload`)의 판정 — 서류 칸, 파일 검사, 완료 팝업(W10·W15), 링크 기한(W14)
 *
 * 관리 화면(「콜팝 가입신청」)의 목록·상세 판정은 `storeApplicationAdmin.js` 다.
 */
// 확장자를 적는 이유: 단위 시험이 앱 번들 밖(node)에서 읽는다 — node ESM 은 상대 경로 확장자를 추론하지 않는다.
import { LOOKUP_REQUEST_CONFIG, httpErrorBody, httpStatusOf, isTransportError } from '../plugins/http/policy.js'
import { formatKstIso } from './kstDate.js'
import { NETWORK_TEXT } from './ownerCopy.js'

/** 화면 주소 — 사장님 서류(사이드 메뉴 없음)와 휴대폰 올리기(공개). 관리 화면 주소는 콜팝 경로 표(`callpopOrg.CALLPOP_PATH`)에 있다 */
export const STORE_APPLICATION_PATH = Object.freeze({
  MINE: '/v/store/applications',
  UPLOAD: '/docs/upload',
})

/** 관리·사장님 API 의 뿌리 — 세션 경로(`ResourceServerConfig.SESSION_AUTH_API_PATHS` 의 `/api/admin/**`) */
export const STORE_APPLICATION_API = '/api/admin/store-applications'

/** 사장님 입구 — 관리자 입구와 주소가 겹치지 않게 `/mine/…` 아래다(§11.3) */
export const MINE_APPLICATION_API = `${STORE_APPLICATION_API}/mine`

/** 휴대폰 올리기 입구 — 공개(표가 열쇠), `/api/owner/*` 아래(§11.4) */
export const DOC_LINK_API = Object.freeze({
  STATUS: '/api/owner/doc-upload/status',
  UPLOAD: '/api/owner/doc-upload',
})

/** 사장님 서류 저장 — `POST …/mine/{applicationId}/docs` multipart */
export function mineDocsUrl(applicationId) {
  return `${MINE_APPLICATION_API}/${applicationId}/docs`
}

/** 사장님이 자기 서류(지금 판)를 본다 */
export function mineDocFileUrl(applicationId, docKind) {
  return `${MINE_APPLICATION_API}/${applicationId}/docs/${docKind}/file`
}

/** 사장님 QR 링크 발급 */
export function mineUploadLinkUrl(applicationId) {
  return `${MINE_APPLICATION_API}/${applicationId}/upload-links`
}

/** 세션 액션 id — 서버 권한 SQL 의 `sys_action` 과 같은 글자(§11.9) */
export const STORE_APPLICATION_ACTION = Object.freeze({
  /** 사장님 — `GET …/mine`, 라우트 `/v/store/applications` 의 게이트이기도 하다 */
  MINE: 'StoreApplicationMine',
  MINE_DOC_UPLOAD: 'StoreApplicationMineDocUpload',
  MINE_DOC_VIEW: 'StoreApplicationMineDocView',
  MINE_UPLOAD_LINK: 'StoreApplicationMineUploadLink',
  /** 내부 + 콜팝 역할 — 목록(라우트 `/v/callpop/applications` 의 게이트)·건수·상세 */
  SEARCH: 'StoreApplicationSearch',
  SUMMARY: 'StoreApplicationSummary',
  DETAIL: 'StoreApplicationDetail',
  /** 내부(admin·mng)만 */
  DOC_VIEW: 'StoreApplicationDocView',
  DOC_CONFIRM: 'StoreApplicationDocConfirm',
  CORRECTION: 'StoreApplicationCorrection',
  DOC_UPLOAD: 'StoreApplicationDocUpload',
  UPLOAD_LINK: 'StoreApplicationUploadLink',
  PG_PROGRESS: 'StoreApplicationPgProgress',
  MEMO: 'StoreApplicationMemo',
})

/** 신청 종류 — 서버 enum 이 정본(§11.1). 관리 메뉴 「콜팝 가입신청」은 이 종류만 본다 */
export const APPLICATION_KIND = Object.freeze({ CALLPOP_JOIN: 'CALLPOP_JOIN' })

/** 서류 종류 — 화면의 칸 순서 */
export const DOC_KIND = Object.freeze({
  BUSINESS_REGISTRATION: 'BUSINESS_REGISTRATION',
  ID_CARD: 'ID_CARD',
  BANKBOOK: 'BANKBOOK',
})

export const DOC_KINDS = Object.freeze([DOC_KIND.BUSINESS_REGISTRATION, DOC_KIND.ID_CARD, DOC_KIND.BANKBOOK])

/**
 * 서류 종류의 이름 — 시안·알림톡 `#{서류}` 와 같은 낱말. 서버 정본 appserver `service/storeapplication/StoreApplicationModel.kt` 의
 * `StoreDocKind.label` 사본이다(골든 `tests/unit/storeApplication.test.js` — 정본을 고치면 같이 고친다)
 */
const DOC_KIND_LABEL = Object.freeze({
  [DOC_KIND.BUSINESS_REGISTRATION]: '사업자등록증',
  [DOC_KIND.ID_CARD]: '신분증',
  [DOC_KIND.BANKBOOK]: '통장사본',
})

/**
 * 서류 칸 밑 안내 — 신분증은 주민등록번호가 보이는 앞면 전체를 받는다(법인 매장도 같다 — W7·W19). 그래서 「뒷자리를 가려 주세요」 같은
 * 안내를 두지 않는다. 안내가 없는 종류는 빈 글자
 */
const DOC_KIND_HINT = Object.freeze({
  [DOC_KIND.ID_CARD]: '주민등록번호가 보이는 신분증 앞면 전체를 올려 주세요.',
})

/** 서류 종류의 칸 안내 — 없으면 빈 글자 */
export function docKindHint(docKind) {
  return DOC_KIND_HINT[docKind] || ''
}

/** 신청의 서류 상태(계산값 — 서버가 준다). 판정 순서는 서버의 것이다: 보완 요청 > 미제출·일부 > 확인 완료 > 재확인 필요 > 확인 대기 */
export const DOC_STATUS = Object.freeze({
  CORRECTION: 'CORRECTION',
  NONE: 'NONE',
  PARTIAL: 'PARTIAL',
  CONFIRMED: 'CONFIRMED',
  RECHECK: 'RECHECK',
  WAITING: 'WAITING',
})

/** 서류 한 장의 상태 */
export const DOC_ITEM_STATUS = Object.freeze({
  PENDING: 'PENDING',
  CONFIRMED: 'CONFIRMED',
  CORRECTION: 'CORRECTION',
  DELETED: 'DELETED',
})

/**
 * 서류 파일 — JPG·PNG·PDF, 한 장 10MB 까지(§11.3 — 넘으면 400 `FILE_INVALID`). 서버 정본 appserver
 * `service/storeapplication/StoreApplicationRules.kt` `MAX_FILE_BYTES` 와 `StoreApplicationModel.kt` `DocFormat.contentType` 의 사본이다
 * (골든 `tests/unit/storeApplication.test.js`). 서버는 이름이 아니라 첫 바이트로 가린다 — 화면은 보내기 전에 먼저 볼 뿐이다
 */
export const DOC_FILE_MAX_BYTES = 10 * 1024 * 1024
export const DOC_FILE_TYPES = Object.freeze(['image/jpeg', 'image/png', 'application/pdf'])
const DOC_FILE_EXTENSIONS = Object.freeze(['.jpg', '.jpeg', '.png', '.pdf'])

/** 파일 고르기 칸의 `accept` — 휴대폰은 이 목록이면 카메라 촬영도 고를 수 있다 */
export const DOC_FILE_ACCEPT = DOC_FILE_TYPES.concat(DOC_FILE_EXTENSIONS).join(',')

/** multipart 칸 이름(§11.3·§11.4) */
export const DOC_UPLOAD_PART = Object.freeze({ TICKET: 't', DOC_KIND: 'docKind', FILE: 'file' })

/** 프로그램 다운로드 — 정식 다운로드 페이지(요청 시안 그대로, §1) */
export const POS2LITE_DOWNLOAD_URL = 'https://pos2lite-releases.web.app/'

/**
 * 서류 올리기·링크 요청의 axios 옵트아웃 — 실패는 화면이 그 칸에서 한 번만 말한다(토스트 끔). 휴대폰 화면은 세션이 없어 403 이 세션을 끝낼
 * 일이 없다(`logoutOn403:false` — 공개 입구의 403 은 앞단의 답이다).
 */
export const DOC_REQUEST_CONFIG = Object.freeze({ showErrorMessage: false, logoutOn403: false })

/**
 * 로그인 뒤 남은 서류 조회(`LoginView`)의 axios config — 화면 본체가 아닌 부가 조회라 가벼운 조회 프로필(10초·잠금 로딩 없음·토스트 없음 —
 * `policy.LOOKUP_REQUEST_CONFIG`)이고, 막 연 세션을 403 으로 끝내지 않는다. 실패·시간 초과면 부르는 쪽은 조용히 처음 화면으로 간다
 * (느린 서버에서 로그인 화면이 1분 멈추던 자리 — 리뷰 1회 5)
 */
export const PENDING_APPLICATION_READ_CONFIG = Object.freeze(
  Object.assign({}, LOOKUP_REQUEST_CONFIG, { logoutOn403: false })
)

/** 화면 문구 — i18n 키(한국어 문장 그대로) */
export const STORE_APPLICATION_TEXT = Object.freeze({
  // 서류 상태(신청)
  statusCorrection: '보완 요청',
  statusNone: '서류 미제출',
  statusPartial: '일부 제출',
  statusConfirmed: '확인 완료',
  statusRecheck: '재확인 필요',
  statusWaiting: '확인 대기',
  // 서류 한 장
  itemNone: '미제출',
  itemPending: '확인 대기',
  itemConfirmed: '확인 완료',
  itemCorrection: '다시 올려 주세요',
  /** 보관 기간이 지나 지운 판(W11) — 받은 것으로 세지만 파일은 없다. [보기] 없이 다시 올릴 수 있다(§11.11) */
  itemDeleted: '보관 기간이 지나 지웠어요',
  // 사장님 화면
  mineIntro: '사업자등록증·신분증·통장사본을 올려 주세요. 하나씩 나눠 올려도 되고, 나중에 이어서 올려도 돼요.',
  mineEmpty: '이 매장에는 서류를 낼 신청이 없어요.',
  loadFailed: '신청을 읽지 못했어요. 잠시 뒤 다시 해 주세요.',
  reload: '다시 읽기',
  choose: '파일 고르기',
  chooseAgain: '다시 고르기',
  save: '저장',
  view: '보기',
  saved: '저장했어요.',
  saveFailed: '저장하지 못했어요. 잠시 뒤 다시 해 주세요.',
  fileInvalid: 'JPG·PNG·PDF 파일만, 한 장에 10MB 까지 올릴 수 있어요.',
  phoneUpload: '휴대폰으로 올리기',
  // QR(사장님·내부 공용)
  linkTitle: '휴대폰으로 서류 올리기',
  linkSub: '휴대폰 카메라로 QR 을 찍으면 서류 올리기 화면이 열려요. 로그인하지 않고 바로 올릴 수 있어요.',
  linkPrivacy: 'QR 에는 이름·전화번호 같은 정보가 들어 있지 않아요.',
  linkCopy: '주소 복사',
  linkCopied: '주소를 복사했어요.',
  linkCopyFailed: '복사하지 못했어요. 주소를 골라 복사해 주세요.',
  linkFailed: '링크를 만들지 못했어요. 잠시 뒤 다시 해 주세요.',
  close: '닫기',
  // 휴대폰 화면
  uploadIntro: '서류를 하나씩 올려 주세요. 사진을 찍어도 되고 파일을 골라도 돼요.',
  upload: '올리기',
  uploaded: '올렸어요.',
  linkUnusableTitle: '이 링크는 쓸 수 없어요',
  linkUnusableSub: '기한이 지났거나 없는 링크예요. 로그인해서 올려 주세요.',
  loginToUpload: '로그인해서 올리기',
  /** 사장님 화면의 연결 말 — 로그인·가입과 같은 문장(`ownerCopy.NETWORK_TEXT`) */
  networkFailed: NETWORK_TEXT,
  // 파일 보기
  viewFailed: '서류를 열지 못했어요. 잠시 뒤 다시 해 주세요.',
  // 로그인 뒤 안내
  pendingConfirm: '서류 올리러 가기',
  pendingLater: '나중에',
  pendingCorrection: '다시 올려 주셔야 할 서류가 있어요. 지금 올릴까요?',
})

/**
 * 완료 팝업(W10·W15) — PC 는 요청 문구 그대로(§1), 휴대폰은 셋째 줄만 「매장 PC 에서 내려받아 설치」로 바꾸고 다운로드 주소와 [주소 복사]를
 * 보인다. 팝업은 서버가 「처음 3종이 다 찼다」(`justCompleted`)고 할 때만 뜬다 — 일부 저장·실패에는 뜨지 않는다.
 */
export const COMPLETION_COPY = Object.freeze({
  line1: '가입이 완료되었습니다.',
  line2: '신청 서류 3종이 정상적으로 접수되었습니다.',
  line3Pc: '아래 프로그램 다운로드 버튼을 눌러 POS2 Lite를 다운로드한 후 설치해 주세요.',
  line3Mobile: 'POS2 Lite 는 매장 PC 에서 내려받아 설치해 주세요',
  confirm: '확인',
  download: '프로그램 다운로드',
  copyAddress: '주소 복사',
})

/** 완료 팝업의 세 줄 — 휴대폰이면 셋째 줄이 다르다 */
export function completionLinesOf(mobile) {
  return [COMPLETION_COPY.line1, COMPLETION_COPY.line2, mobile ? COMPLETION_COPY.line3Mobile : COMPLETION_COPY.line3Pc]
}

/** 서류 종류의 이름 — 모르는 종류는 받은 글자 그대로(서버가 종류를 먼저 늘려도 빈칸이 되지 않는다) */
export function docKindLabel(docKind) {
  return DOC_KIND_LABEL[docKind] || (docKind ? String(docKind) : '')
}

/** 서류 종류 목록 → 「신분증, 통장사본」(칸 순서로) */
export function docKindsText(docKinds) {
  const kinds = Array.isArray(docKinds) ? docKinds : []
  return DOC_KINDS.filter((kind) => kinds.indexOf(kind) > -1)
    .map(docKindLabel)
    .join(', ')
}

/** 신청 상태 → 말·색(색 이름은 화면의 배지 클래스 끝 조각) */
const DOC_STATUS_VIEWS = Object.freeze({
  [DOC_STATUS.CORRECTION]: Object.freeze({ label: STORE_APPLICATION_TEXT.statusCorrection, tone: 'danger' }),
  [DOC_STATUS.NONE]: Object.freeze({ label: STORE_APPLICATION_TEXT.statusNone, tone: 'muted' }),
  [DOC_STATUS.PARTIAL]: Object.freeze({ label: STORE_APPLICATION_TEXT.statusPartial, tone: 'warning' }),
  [DOC_STATUS.CONFIRMED]: Object.freeze({ label: STORE_APPLICATION_TEXT.statusConfirmed, tone: 'success' }),
  [DOC_STATUS.RECHECK]: Object.freeze({ label: STORE_APPLICATION_TEXT.statusRecheck, tone: 'warning' }),
  [DOC_STATUS.WAITING]: Object.freeze({ label: STORE_APPLICATION_TEXT.statusWaiting, tone: 'info' }),
})

/** 상태 카드·거르기의 차례 — 먼저 손볼 것부터(시안 「우선 확인할 상태」) */
export const DOC_STATUS_ORDER = Object.freeze([
  DOC_STATUS.WAITING,
  DOC_STATUS.RECHECK,
  DOC_STATUS.CORRECTION,
  DOC_STATUS.NONE,
  DOC_STATUS.PARTIAL,
  DOC_STATUS.CONFIRMED,
])

/** 신청 상태 → `{label, tone}`. 모르는 상태는 받은 글자 그대로 */
export function docStatusViewOf(docStatus) {
  return DOC_STATUS_VIEWS[docStatus] || { label: docStatus ? String(docStatus) : '', tone: 'muted' }
}

/** 신청 상태의 이름 — 카드·선택지 */
export function docStatusLabel(docStatus) {
  return docStatusViewOf(docStatus).label
}

/** 서류 한 장의 상태 → `{label, tone}`. 낸 판이 없으면 「미제출」 */
const DOC_ITEM_VIEWS = Object.freeze({
  [DOC_ITEM_STATUS.PENDING]: Object.freeze({ label: STORE_APPLICATION_TEXT.itemPending, tone: 'info' }),
  [DOC_ITEM_STATUS.CONFIRMED]: Object.freeze({ label: STORE_APPLICATION_TEXT.itemConfirmed, tone: 'success' }),
  [DOC_ITEM_STATUS.CORRECTION]: Object.freeze({ label: STORE_APPLICATION_TEXT.itemCorrection, tone: 'danger' }),
  [DOC_ITEM_STATUS.DELETED]: Object.freeze({ label: STORE_APPLICATION_TEXT.itemDeleted, tone: 'muted' }),
})

const NO_DOC_VIEW = Object.freeze({ label: STORE_APPLICATION_TEXT.itemNone, tone: 'muted' })

/** 서류 한 장의 상태 → `{label, tone}`(없으면 미제출) */
export function docItemViewOf(status) {
  if (!status) return NO_DOC_VIEW
  return DOC_ITEM_VIEWS[status] || { label: String(status), tone: 'muted' }
}

/**
 * 지운 판인가(W11) — 서버가 그 종류에 지운 시각(`deletedAt`)을 싣는다. 보완 요청 중이라 상태가 `CORRECTION` 이어도 파일은 없다
 */
function isPurgedDoc(doc) {
  return !!doc && (!!doc.deletedAt || doc.status === DOC_ITEM_STATUS.DELETED)
}

/**
 * 열어 볼 수 있는 낸 서류인가 — 판이 있고(안 낸 종류는 상태가 `null` 인 칸으로 온다, §11.11) 지운 판(W11)이 아니다. 건수(n/3)가 아니다 —
 * 건수와 신청 상태는 서버 값(`docCount`·`docStatus`)이다(지운 판도 받은 것으로 센다)
 */
function isHeldDoc(doc) {
  return !!doc && !!doc.status && !isPurgedDoc(doc)
}

/**
 * **서류 세 칸** — 종류마다 그 종류의 지금 판(`docs` 의 같은 종류 — 신청 응답에는 지금 판만 오고, 안 낸 종류는 상태·판·시각이 `null` 인
 * 칸으로 온다 — §11.11)과 보완 요청 여부. 보완 요청 중인 종류는
 * 판의 상태와 무관하게 「다시 올려 주세요」다(`requested`). 순수.
 *
 * @param {?Array<{docKind: string, status: string, version?: number, uploadedAt?: string}>} docs
 * @param {?Array<string>} requested 보완 요청 중인 종류
 * @returns {Array<{docKind: string, label: string, hint: string, doc: ?object, held: boolean, requested: boolean,
 *   view: {label: string, tone: string}, purgedNote: string}>}
 */
export function docSlotsOf(docs, requested) {
  const rows = Array.isArray(docs) ? docs : []
  const asked = Array.isArray(requested) ? requested : []

  return DOC_KINDS.map((docKind) => {
    const doc = rows.find((row) => row && row.docKind === docKind) || null
    const isRequested = asked.indexOf(docKind) > -1
    const view = isRequested ? docItemViewOf(DOC_ITEM_STATUS.CORRECTION) : docItemViewOf(doc && doc.status)
    // 지운 판인데 상태 말이 그것을 말하지 않으면(보완 요청 중) 「보관 기간이 지나 지웠어요」를 함께 보인다
    const purgedNote =
      isPurgedDoc(doc) && view.label !== STORE_APPLICATION_TEXT.itemDeleted ? STORE_APPLICATION_TEXT.itemDeleted : ''

    return {
      docKind,
      label: docKindLabel(docKind),
      hint: docKindHint(docKind),
      doc,
      held: isHeldDoc(doc),
      requested: isRequested,
      view,
      purgedNote,
    }
  })
}

/** `서류 1/3` */
export function docCountText(count) {
  return `서류 ${count}/${DOC_KINDS.length}`
}

/**
 * 서류 칸 — 「서류 1/3」. n 은 서버가 센 `docCount` 다(신청·휴대폰 상태·관리 행·상세 머리가 같은 칸을 싣는다 — 화면이 다시 세지 않는다,
 * §11.11). 건수가 없으면 빈 글자
 */
export function docCountTextOf(head) {
  const count = head && head.docCount
  return typeof count === 'number' && Number.isFinite(count) && count >= 0 ? docCountText(Math.floor(count)) : ''
}

/** 신청 화면의 제목 — 「콜팝 가입 서류」(종류 이름은 서버가 준다) */
export function applicationTitleOf(kindName) {
  return kindName ? `${kindName} 서류` : '서류'
}

/** 보완 요청 줄 — 「다시 올려 주셔야 할 서류: 신분증, 통장사본」. 요청이 없으면 빈 글자 */
export function correctionLineOf(requested) {
  const text = docKindsText(requested)
  return text ? `다시 올려 주셔야 할 서류: ${text}` : ''
}

/** 아직 사장님이 할 일이 남은 신청인가 — 미제출·일부 제출·보완 요청 */
export function isPendingApplication(application) {
  const status = application && application.docStatus
  return status === DOC_STATUS.NONE || status === DOC_STATUS.PARTIAL || status === DOC_STATUS.CORRECTION
}

/** 할 일이 남은 첫 신청 — 없으면 `null`(로그인 뒤 안내의 근거) */
export function pendingApplicationOf(applications) {
  const rows = Array.isArray(applications) ? applications : []
  return rows.find(isPendingApplication) || null
}

/**
 * **로그인 뒤 안내 한 줄** — 보완 요청이면 「다시 올려 주셔야 할 …」, 아니면 「아직 내지 않은 서류가 있어요(서류 n/3)」. 순수.
 *
 * @param {{docStatus: string, docs?: Array}} application [pendingApplicationOf] 의 답
 */
export function pendingNoticeOf(application) {
  if (application.docStatus === DOC_STATUS.CORRECTION) return STORE_APPLICATION_TEXT.pendingCorrection
  const count = docCountTextOf(application)
  return count
    ? `아직 내지 않은 서류가 있어요(${count}). 지금 올릴까요?`
    : '아직 내지 않은 서류가 있어요. 지금 올릴까요?'
}

/** `GET …/mine` 200 `{applications}` → 신청 목록(형식 밖이면 빈 목록) */
export function applicationsOf(body) {
  const list = body && Array.isArray(body.applications) ? body.applications : []
  return list.filter((row) => row && typeof row === 'object' && row.applicationId != null)
}

/** 서류 저장 200 `{application, justCompleted}` → 같은 모양(신청이 없으면 `null`) */
export function docSaveResultOf(body) {
  const application = body && body.application && typeof body.application === 'object' ? body.application : null
  return application ? { application, justCompleted: body.justCompleted === true } : null
}

/**
 * 휴대폰 화면의 상태 `{storeNm, kindName, docStatus, docCount, docs, requested, expiresAt}`(+ 올리기 답의 `justCompleted`) → 같은 모양. 형식 밖이면
 * `null`. 이미 낸 서류의 내용은 오지 않는다(W12) — 종류와 상태뿐이다.
 */
export function linkStatusOf(body) {
  if (!body || typeof body !== 'object' || !body.docStatus) return null
  return {
    storeNm: typeof body.storeNm === 'string' ? body.storeNm : '',
    kindName: typeof body.kindName === 'string' ? body.kindName : '',
    docStatus: body.docStatus,
    docCount: typeof body.docCount === 'number' ? body.docCount : null,
    docs: Array.isArray(body.docs) ? body.docs : [],
    requested: Array.isArray(body.requested) ? body.requested : [],
    expiresAt: typeof body.expiresAt === 'string' ? body.expiresAt : null,
    justCompleted: body.justCompleted === true,
  }
}

/** QR 링크 200 `{link, expiresAt}` → 같은 모양. 링크가 없으면 `null` */
export function uploadLinkOf(body) {
  if (!body || typeof body.link !== 'string' || !body.link.trim()) return null
  return { link: body.link, expiresAt: typeof body.expiresAt === 'string' ? body.expiresAt : null }
}

/** 링크 기한(KST) — 「11월 5일 18:30」. 읽을 수 없으면 빈 글자 */
export function expiresTextOf(iso) {
  return formatKstIso('M월 d일 HH:mm', iso) || ''
}

/** 「이 링크는 11월 5일 18:30까지 몇 번이고 쓸 수 있어요.」 — 기한을 모르면 빈 글자 */
export function linkExpiresLineOf(iso) {
  const text = expiresTextOf(iso)
  return text ? `이 링크는 ${text}까지 몇 번이고 쓸 수 있어요.` : ''
}

/** 휴대폰 링크가 쓸 수 없는 링크인가 — 없거나(404 `LINK_NOT_FOUND`) 기한이 지났다(410 `LINK_EXPIRED`) */
export function isLinkUnusable(error) {
  const status = httpStatusOf(error)
  return status === 404 || status === 410
}

/**
 * **파일을 보내기 전에** — 형식(JPG·PNG·PDF)과 크기(10MB)를 본다. 문제가 없으면 `null`, 있으면 그 말. 형식은 브라우저가 준 종류
 * (`type`)로, 종류가 비었으면(일부 휴대폰) 이름의 확장자로 본다. 서버가 정본이다(400 `FILE_INVALID`). 순수.
 *
 * @param {?{name?: string, type?: string, size?: number}} file
 * @returns {?string}
 */
export function docFileProblemOf(file) {
  if (!file) return STORE_APPLICATION_TEXT.fileInvalid
  const type = typeof file.type === 'string' ? file.type.toLowerCase() : ''
  const name = typeof file.name === 'string' ? file.name.toLowerCase() : ''
  const typed = type ? DOC_FILE_TYPES.indexOf(type) > -1 : DOC_FILE_EXTENSIONS.some((ext) => name.endsWith(ext))
  const sized = typeof file.size === 'number' && file.size > 0 && file.size <= DOC_FILE_MAX_BYTES

  return typed && sized ? null : STORE_APPLICATION_TEXT.fileInvalid
}

/** 서류 올리기의 multipart 칸 — 휴대폰 화면은 표를 맨 앞에 싣는다(표는 본문으로만 보낸다 — §11.4) */
export function docUploadPartsOf(docKind, file, ticket) {
  const parts = [
    [DOC_UPLOAD_PART.DOC_KIND, docKind],
    [DOC_UPLOAD_PART.FILE, file],
  ]
  return ticket ? [[DOC_UPLOAD_PART.TICKET, ticket]].concat(parts) : parts
}

/**
 * **서류 올리기 실패 → 그 칸의 한 줄** — 파일 거절(400 `FILE_INVALID`)은 형식·크기 말, 전송 실패는 연결 말, 그 밖은 「저장하지 못했어요」.
 * 휴대폰 링크의 404·410 은 여기 오지 않는다 — 화면이 [isLinkUnusable] 로 먼저 가른다. 순수.
 */
export function docSaveFailureText(error) {
  const body = httpErrorBody(error)
  if (body && body.code === 'FILE_INVALID') return STORE_APPLICATION_TEXT.fileInvalid
  if (isTransportError(error)) return STORE_APPLICATION_TEXT.networkFailed
  return STORE_APPLICATION_TEXT.saveFailed
}
